import { Alert, Box, Skeleton, Typography } from "@mui/material";
import React from "react";
import {
  FeeAnalyticsBarChart, DailyFeeTrendChart, MonthlyFeeTrendChart,
  AttendanceByGradeChart, FacultyAttendanceByCategoryChart,
} from "../index";

import { useAdminDashboardData } from "../hooks/useAdminDashboardData";
import { formatLastUpdated } from "../hooks/useCachedDashboard";
import { useAcademicYearContext } from "../../../context/AacademicYearContext";
import { AdminDashboardHeader } from "./components/AdminDashboardHeader";
import { AttentionPanel } from "./components/AttentionPanel";
import { TrendStatCard } from "./components/TrendStatCard";
import { adminDisplayName, statCards } from "./mockData";
import { formatINRCompact } from "../../../utils/formatCurrency";

export const AdminDashboard = () => {

  const { currentAcademicYear } = useAcademicYearContext();
  console.log("In Dashboard currentAcademicYear: " + currentAcademicYear)

  const {
    loading,
    refreshing,
    error,
    fetchedAt,
    refresh,
    attentionItems,
    studentOverview,
    feeOverview,
    feeByGrade,
    dailyFeeTrend,
    monthlyFeeTrend,
    attendanceByGrade,
    facultyAttendance,
  } = useAdminDashboardData();

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Full-page error only when there is nothing cached to show.
  if (error && !studentOverview) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2, mt: 1 }}>
        {error}
      </Alert>
    );
  }

  // Overview cards: swap in real values where we have them; "Attendance Today"
  // and everything else about these cards (trend, icon, route...) stays mock.
  const overviewCards = statCards.map((card) => {
    switch (card.id) {
      case "total-students":
        return { ...card, value: studentOverview!.activeStudents.toLocaleString() };
      case "admissions-in-progress":
        return { ...card, value: studentOverview!.admissionsInProgress.toLocaleString() };
      case "fee-collected":
        return { ...card, value: `₹${formatINRCompact(feeOverview!.collected)}` };
      case "current-year-dues":
        return { ...card, value: `₹${formatINRCompact(feeOverview!.currentYearDues)}` };
      case "previous-year-dues":
        return { ...card, value: `₹${formatINRCompact(feeOverview!.previousYearDues)}` };
      default:
        return card;
    }
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 }, maxWidth: 1600, mx: "auto", width: "100%", px: { xs: 0.5, md: 1 }, pb: 3 }}>
      <AdminDashboardHeader
        adminName={adminDisplayName}
        lastUpdatedLabel={formatLastUpdated(fetchedAt)}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}. Showing the last loaded data.
        </Alert>
      )}

      <AttentionPanel items={attentionItems} />

      <DashboardSection title="Overview">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              xl: "repeat(6, 1fr)",
            },
            gap: 2,
          }}
        >
          {overviewCards.map((card) => (
            <TrendStatCard key={card.id} data={card} />
          ))}
        </Box>
      </DashboardSection>

      <DashboardSection title="Fees">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <FeeAnalyticsBarChart data={feeByGrade} />
          <MonthlyFeeTrendChart data={monthlyFeeTrend} />
          <Box sx={{ gridColumn: { md: "1 / -1" } }}>
            <DailyFeeTrendChart data={dailyFeeTrend} />
          </Box>
        </Box>
      </DashboardSection>

      <DashboardSection title="Attendance Overview">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <AttendanceByGradeChart data={attendanceByGrade} />
          <FacultyAttendanceByCategoryChart data={facultyAttendance} />
        </Box>
      </DashboardSection>
    </Box>
  );
};

// One heading style for every dashboard section.
const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box component="section">
    <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

// Placeholder shapes in the dashboard's real layout while data loads.
const DashboardSkeleton = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 }, maxWidth: 1600, mx: "auto", width: "100%", px: { xs: 0.5, md: 1 }, pt: 1.5 }}>
    <Box>
      <Skeleton width={280} height={40} />
      <Skeleton width={420} />
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2 }}>
      {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={150} sx={{ borderRadius: 3 }} />)}
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 3 }} />)}
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
      {[0, 1].map((i) => <Skeleton key={i} variant="rounded" height={360} sx={{ borderRadius: 3 }} />)}
    </Box>
  </Box>
);
